import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy, limit, doc, updateDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hour, min] = timeStr.split(':');
  const h = parseInt(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12; // Converts "00" to "12" and "13" to "1"
  return `${displayHour}:${min} ${ampm}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // Split the YYYY-MM-DD string to avoid timezone "shift" issues
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
};

const fetchGoogleEvents = async (accessToken, calendarId) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  const timeMin = date.toISOString();
  try {
    const encodedId = encodeURIComponent(calendarId);
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodedId}/events?timeMin=${timeMin}&maxResults=5&singleEvents=true&orderBy=startTime`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const data = await response.json();
    return data.items || [];
  } catch (err) {
    console.error("Google Fetch Error:", err);
    return [];
  }
};

const Dashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [meals, setMeals] = useState([]);
  const [localEvents, setLocalEvents] = useState([]);
  const { user, googleToken } = useAuth();
  const [kidHighlight, setKidHighlight] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]); 

  // 1. FETCH GOOGLE EVENTS
  useEffect(() => {
    if (!user || !googleToken) return;

    const getSummary = async () => {
      try {
        // 1. Get your saved IDs from Firestore first
        const docRef = doc(db, 'users', user.uid, 'settings', 'calendar');
        const docSnap = await getDoc(docRef);
        
        // Default to 'primary' if nothing is saved yet
        let idsToFetch = ['primary'];
        if (docSnap.exists() && docSnap.data().selectedCalIds) {
          idsToFetch = docSnap.data().selectedCalIds;
        }

        // 2. Fetch only those specific calendars
        const results = await Promise.all(
          idsToFetch.map(id => fetchGoogleEvents(googleToken, id))
        );

        // 3. Flatten and format
        const formatted = results.flat().map(ge => ({
          id: ge.id,
          title: ge.summary,
          date: (ge.start.dateTime || ge.start.date).split('T')[0],
          source: 'Google',
          time: ge.start.dateTime ? new Date(ge.start.dateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'All Day'
        }));

        setGoogleEvents(formatted);
      } catch (e) {
        console.error("Dashboard Google Sync Failed", e);
      }
    };

    getSummary();
  }, [user, googleToken]);

  // 2. COMBINE LOCAL AND GOOGLE
  const combinedEvents = [...localEvents, ...googleEvents]
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 4);

  // 3. FIREBASE LISTENERS
  useEffect(() => {
    if (!user) return;

    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const todayLabel = dayLabels[new Date().getDay()];

    // Tasks: Just the top 4
    const q = query(
      collection(db, 'tasks'), 
      where('userId', '==', user.uid), 
      where('completed', '==', false), 
      orderBy('priority', 'asc'),
      limit(4)
    );

    const unsubTasks = onSnapshot(q, (snap) => {
      setTasks(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });  

    // Events: Next 4
    // This one listener now pulls Family Events AND Kid Appointments!
    const unsubEvents = onSnapshot(
      query(
        collection(db, 'calendar'), 
        where('userId', '==', user.uid), 
        where('date', '>=', today), 
        orderBy('date', 'asc'), 
        limit(4)
      ),
      (snap) => {
        setLocalEvents(snap.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          // If it's a kid event, prefix the title for the UI
          displayTitle: doc.data().kidName ? `${doc.data().kidName}: ${doc.data().title}` : doc.data().title
        })));
      }
    );

    const unsubMeal = onSnapshot(
      query(
        collection(db, 'meals'), 
        where('userId', '==', user.uid), 
        where('date', '>=', today), 
        orderBy('date', 'asc'), 
        limit(4)
      ),
      (snap) => {
        const mealList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMeals(mealList);
      }
    );

    const unsubKids = onSnapshot(query(collection(db, 'kids'), where('userId', '==', user.uid)), (snap) => {

      snap.docs.forEach(kidDoc => {
        const kidName = kidDoc.data().name;
        const kidId = kidDoc.id;

        // Direct listener for Routines
        onSnapshot(query(
          collection(db, 'kids', kidId, 'routines'),
          where('days', 'array-contains', todayLabel),
          where('time', '>=', currentTime),
          orderBy('time', 'asc')
        ), (schedSnap) => {
          const items = schedSnap.docs.map(d => ({
            id: d.id,
            kidName,
            kidId, 
            ...d.data()
          }));

          setKidHighlight(prev => {
            const others = prev.filter(h => h.kidId !== kidId);
            return [...others, ...items]
              .sort((a, b) => a.time.localeCompare(b.time))
              .slice(0, 4);
          });
        });
      });

    });

      return () => { unsubTasks(); unsubEvents(); unsubMeal(); unsubKids(); };
  }, [user]);

  // Style helper to keep the JSX clean
  const cardClass = "bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300";
  const iconBox = "w-10 h-10 rounded-xl flex items-center justify-center mb-4 shadow-sm border";

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <header className="mb-10">
        <h1 className="text-4xl font-black text-gray-800 tracking-tight">Home</h1>
        <p className="text-gray-400 font-bold text-[10px] md:text-xs uppercase tracking-widest mt-1">
          Here's what is happening today.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* TASKS - Subtle Blue Accent */}
        <div className={cardClass}>
          <div className={`${iconBox} bg-blue-50 border-blue-100 text-blue-500`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Urgent Tasks</h2>
          <ul className="space-y-3">
            {tasks.length > 0 ? tasks.map(t => (
              <li 
                key={t.id} 
                className="group flex items-center gap-3 text-sm font-bold text-gray-700 p-2 rounded-xl hover:bg-gray-50 transition-colors"
              >
                {/* The Checkbox (Now on the left and priority-colored) */}
                <button 
                  onClick={async (e) => {
                    e.stopPropagation();
                    await updateDoc(doc(db, 'tasks', t.id), { completed: true });
                  }}
                  className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md border-2 transition-all bg-white
                    ${t.priority === 1 ? 'border-red-400 hover:bg-red-50 text-red-500' : 
                      t.priority === 2 ? 'border-orange-400 hover:bg-orange-50 text-orange-500' : 
                      'border-blue-300 hover:bg-blue-50 text-blue-400'}
                    text-transparent hover:text-current`}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="4" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>

                {/* Task Name */}
                <span className="flex-1 py-1">{t.name}</span>
              </li>
            )) : (
              <li className="text-sm text-gray-400 italic p-2">All caught up!</li>
            )}
          </ul>
        </div>

        {/* CALENDAR - Subtle Indigo Accent */}
        <div className={cardClass}>
          <div className={`${iconBox} bg-indigo-50 border-indigo-100 text-indigo-500`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Coming Up</h2>
          <div className="space-y-4">
            {combinedEvents.length > 0 ? combinedEvents.map(e => (
              <div key={e.id} className="border-l-2 border-indigo-100 pl-3">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] text-indigo-400 font-bold uppercase">{formatDate(e.date)}</p>
                  {/* If there is a kidName, show it in a small badge */}
                  {e.kidName && (
                    <span className="text-[8px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter border border-indigo-100">
                      {e.kidName}
                    </span>
                  )}
                </div>
                <p className="text-sm font-bold text-gray-700 leading-tight mt-0.5">{e.title}</p>
              </div>
            )) : (
              <p className="text-sm text-gray-400 italic">Clear schedule!</p>
            )}
          </div>
        </div>

        {/* DINNER - Subtle Orange Accent */}
          <div className={cardClass}>
          <div className={`${iconBox} bg-orange-50 border-orange-100 text-orange-500`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
          </div>
          <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">On the Menu</h2>
          
          <div className="space-y-4">
            {meals.length > 0 ? meals.map((m) => (
              <div key={m.id} className="border-l-2 border-orange-100 pl-3">
                <p className="text-[10px] text-orange-400 font-bold uppercase">
                  {formatDate(m.date)}
                </p>
                <p className="text-sm font-black text-gray-800 leading-tight">{m.main}</p>
                {m.side && (
                  <p className="text-[10px] text-gray-400 font-medium">+ {m.side}</p>
                )}
              </div>
            )) : (
              <p className="text-sm text-gray-400 italic">No meals planned</p>
            )}
          </div>
        </div>

        {/* KIDS - Subtle Green Accent */}
        <div className={cardClass}>
          <div className={`${iconBox} bg-green-50 border-green-100 text-green-500`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Kid Highlight</h2>
          {kidHighlight.length > 0 ? kidHighlight.map((item) => (
            <div key={item.id} className="bg-slate-50 rounded-xl p-2 border border-gray-100">
              <div className="flex justify-between items-start">
                <p className="text-[9px] text-green-600 font-bold uppercase">{item.kidName}</p>
                <p className="text-[9px] text-gray-400 font-bold">{formatTime(item.time)}</p>
              </div>
              <p className="text-xs font-bold text-gray-700 leading-tight">{item.activity}</p>
            </div>
          )) : (
            <p className="text-sm text-gray-400 italic">No more routines today!</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;